import * as rudderstack from "@utils/rudderstack";
import { useData } from "../../../runtime/provider";
import { RouterInputs, RouterOutputs, trpc } from "@utils/trpc";
import { useMemo } from "react";
import { captureException } from "@utils/captureException";
import { useMutation, useQuery } from "@tanstack/react-query";
import { NoAuthError } from "@utils/NoAuthError";
import {
  ArtistProfileQueryData,
  artistProfileQueryKey,
} from "@hooks/trpc/useArtists";
import {
  libraryQueryKey,
  type LibraryQueryData,
} from "@hooks/trpc/useLibrary/queryFunctions";
import {
  followingsQuery,
  followingsQueryKey,
  type FollowingsQueryData,
} from "./queryFunctions";

/**
 * Get the list of followed artists for the current user.
 */
export const useFollowings = (refetchOnMount: boolean = false) => {
  const data = useData();
  return useQuery({
    ...followingsQuery(data),
    refetchOnMount,
  });
};

type MarkAsFollowedInput =
  RouterInputs["user"]["artistSubscriptions"]["markAsFollowed"];
type MarkAsFollowedOutput =
  RouterOutputs["user"]["artistSubscriptions"]["markAsFollowed"];

/**
 * All follow/subscribe interactions for an artist. Optimistic updates use
 * `setQueryDataDurable` so they survive a kill-and-relaunch and a bad-
 * internet refetch — the durable SQLite cache stays in sync with the
 * post-mutation state, and `withDurableCache.catch` returns that state on
 * a failed refetch instead of reverting.
 */
export const useFollow = (artistId: string, refetchOnMount?: boolean) => {
  const data = useData();
  const { queryClient } = data;
  const { setQueryDataDurable } = data.cache;
  const { data: followings } = useFollowings(refetchOnMount);

  // Synchronous read of `data.auth.currentUser()?.uid` — Firebase persists locally,
  // so this is available on cold start. The uid is used for the optimistic
  // `FollowingItem.userId` field on the wire shape (NOT for SQLite cache
  // scoping — logout wipes both react-query and the SQLite db).
  const uid = data.auth.currentUser()?.uid ?? "";
  const followingsKey = followingsQueryKey();

  const invalidateOnSettled = () => {
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: followingsKey,
      }),
      queryClient.invalidateQueries({
        queryKey: libraryQueryKey(),
      }),
    ]).catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "follow",
        operation: "invalidate-following-and-library",
        tags: { artistId },
      });
    });
  };

  const unfollowMutation =
    trpc.user.artistSubscriptions.markAsUnfollowed.useMutation({
      onSettled: invalidateOnSettled,
      onMutate: () => {
        setQueryDataDurable<FollowingsQueryData>(
          queryClient,
          followingsKey,
          (prev) => {
            if (!prev) return prev;
            return prev.filter((x) => x.artistId !== artistId);
          },
        );
        // Remove artist from library (durable so the optimistic remove
        // survives a refetch failure).
        setQueryDataDurable<LibraryQueryData>(
          queryClient,
          libraryQueryKey(),
          (prev) => {
            if (!prev) return prev;
            return prev.filter((x) => x.id !== artistId);
          },
        );

        void rudderstack.track("unfollow_artist", {
          resourceType: "artist",
          resourceId: artistId,
        });
      },
    });

  const unsubscribeMutation =
    trpc.user.artistSubscriptions.markAsUnsubscribed.useMutation({
      onSettled: invalidateOnSettled,
      onMutate: () => {
        setQueryDataDurable<FollowingsQueryData>(
          queryClient,
          followingsKey,
          (prev) => {
            if (!prev) return prev;
            return prev.map((x) => {
              if (x.artistId === artistId) {
                return { ...x, type: "follow" };
              }
              return x;
            });
          },
        );

        void rudderstack.track("leave_artist", {
          resourceType: "artist",
          resourceId: artistId,
        });
      },
    });

  const subscribeMutation =
    trpc.user.artistSubscriptions.markAsSubscribed.useMutation({
      onSettled: invalidateOnSettled,
      onMutate: () => {
        setQueryDataDurable<FollowingsQueryData>(
          queryClient,
          followingsKey,
          (prev) => {
            if (!prev) return prev;
            return prev.map((x) => {
              if (x.artistId === artistId) {
                return { ...x, type: "subscribe" };
              }
              return x;
            });
          },
        );
      },

      onSuccess: () => {
        void rudderstack.track("join_artist", {
          resourceType: "artist",
          resourceId: artistId,
        });
      },
    });

  const followMutation = useMutation<
    MarkAsFollowedOutput,
    Error,
    MarkAsFollowedInput
  >({
    mutationFn: (input: MarkAsFollowedInput) => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("followMutation mutationFn");
      }
      return data.api.user.artistSubscriptions.markAsFollowed.mutate(input);
    },
    onSettled: invalidateOnSettled,
    onMutate: () => {
      setQueryDataDurable<FollowingsQueryData>(
        queryClient,
        followingsKey,
        (prev) => {
          // Include `userId` so the optimistic row passes followingItemSchema
          // on a subsequent durable-cache read. The pre-migration code
          // omitted it; that bug is fixed here.
          const next = { userId: uid, artistId, type: "follow" as const };
          return [...(prev || []), next];
        },
      );
      // Add artist to library (durable so the optimistic add survives a
      // refetch failure).
      const artistData = queryClient.getQueryData<ArtistProfileQueryData>(
        artistProfileQueryKey(artistId),
      );
      if (artistData) {
        setQueryDataDurable<LibraryQueryData>(
          queryClient,
          libraryQueryKey(),
          (prev) => {
            if (!prev) return prev;
            return [
              ...prev,
              {
                ...artistData,
                type: "artist",
                created_at: new Date().toISOString(),
              },
            ];
          },
        );
      }

      rudderstack.track("follow_artist", {
        resourceType: "artist",
        resourceId: artistId,
      });
    },
  });

  const isFollowing = useMemo(() => {
    return followings && followings.find((x) => x.artistId === artistId);
  }, [followings, artistId]);

  const followAction = () => {
    if (isFollowing) {
      unfollowMutation.mutateAsync(artistId).catch((error) => {
        captureException(error, {
          channel: "error",
          area: "follow",
          operation: "unfollow-artist",
          tags: { artistId },
        });
      });
    } else {
      followMutation.mutateAsync(artistId).catch((error) => {
        captureException(error, {
          channel: "error",
          area: "follow",
          operation: "follow-artist",
          tags: { artistId },
        });
      });
    }
  };

  const subscribeAction = () => {
    if (isFollowing?.type === "subscribe") {
      unsubscribeMutation.mutateAsync(artistId).catch((error) => {
        captureException(error, {
          channel: "error",
          area: "follow",
          operation: "unsubscribe-artist",
          tags: { artistId },
        });
      });
    } else {
      subscribeMutation.mutateAsync(artistId).catch((error) => {
        captureException(error, {
          channel: "error",
          area: "follow",
          operation: "subscribe-artist",
          tags: { artistId },
        });
      });
    }
  };

  return {
    isFollowing,
    followAction,
    subscribeAction,
  };
};
